import {SegmentationMaskLoader} from '@wandb/weave/components/Panel2/ImageWithOverlays';
import _ from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
import {type PartRefFromObjSchema} from 'src/state/views/types';

import type {PanelObjSchema} from '../../state/views/panel/types';
import {type RunSignature} from '../../types/run';
import {DD_PRIVACY_HIDDEN} from '../../util/datadog/privacy';
import {segmentationMaskColor, type WBFile} from '../../util/media';
import {type FileInfo, useLoadFile} from '../../util/requests';
import {type AllMaskControls, type MaskControl} from '../MediaCard';
import {useLayoutConfig} from '../PanelMediaBrowser/layout/contexts/LayoutConfigContext';
import {useMediaBrowserPanelTrackingContext} from '../PanelMediaBrowser/MediaBrowserPanelTrackingContext';

export type MaskData = {
  key: string;
  wbFile: WBFile;
  classLabels: {[key: number]: string};
};

type MasksOverlayProps = {
  masksRef: React.RefObject<HTMLDivElement | null>;
  mediaKey: string;
  maskData: MaskData[];
  maskOptions?: {maskKeys: string[]; showImage?: boolean};
  controls?: AllMaskControls;
  mediaSize: {width: number; height: number} | null;
  style?: React.CSSProperties;
  runSignature: RunSignature;
  mediaPanelRef: PartRefFromObjSchema<PanelObjSchema>;
};

type SegmentationMaskFromFileProps = {
  style?: React.CSSProperties;
  classLabels:
    | {
        [key: number]: string;
      }
    | undefined;
  maskKey: string;
  mask: WBFile;
  runSignature: RunSignature;
  mediaPanelRef: PartRefFromObjSchema<PanelObjSchema>;
  maskControls?: {
    [classOrAll: string]: MaskControl;
  };
};

/**
 * Renders segmentation mask from WBFile
 */
const SegmentationMaskFromFile: React.FC<SegmentationMaskFromFileProps> =
  React.memo(props => {
    const {maskControls, mask, maskKey, runSignature, style, classLabels} =
      props;

    const [directURL, setDirectURL] = useState<string | undefined>(undefined);
    const {recordMaskViewed} = useMediaBrowserPanelTrackingContext();
    const isPreview = useLayoutConfig()?.isPreview === true;
    const onSuccess = useCallback(
      (_response: unknown, metadata: FileInfo) =>
        setDirectURL(metadata.directUrl),
      []
    );

    const loaderStyle = useMemo(
      () => ({width: '100%', height: '100%', ...style}),
      [style]
    );

    const classOverlay = useMemo(() => maskControls ?? {}, [maskControls]);

    const classState = useMemo(
      () =>
        Object.fromEntries(
          _.map(classLabels, (name, classID) => {
            const maskControl = maskControls?.[Number(classID)];
            const maskControlRGB = maskControl && maskControl.rgb;
            const [r, g, b] =
              maskControlRGB ?? segmentationMaskColor(parseInt(classID, 10));
            segmentationMaskColor(parseInt(classID, 10));
            const color = `rgb(${r}, ${g}, ${b})`;
            return [classID, {name, color}];
          })
        ),
      [classLabels, maskControls]
    );

    useLoadFile(runSignature, mask.path, {
      onSuccess,
      responseType: 'blob',
    });

    if (directURL == null) {
      return <div />;
    }

    return (
      <SegmentationMaskLoader
        key={maskKey}
        style={loaderStyle}
        classOverlay={classOverlay}
        classState={classState}
        directUrl={directURL}
        onMaskRendered={isPreview ? undefined : recordMaskViewed}
      />
    );
  });

export const MasksOverlay: React.FC<MasksOverlayProps> = React.memo(
  ({
    masksRef,
    mediaKey,
    maskData,
    maskOptions,
    controls,
    mediaSize,
    style,
    runSignature,
    mediaPanelRef,
  }) => {
    if (maskData.length === 0 || mediaSize == null) {
      return null;
    }

    return (
      <div
        ref={masksRef as React.RefObject<HTMLDivElement>}
        className="segmentation-mask__container"
        style={style}
        data-dd-privacy={DD_PRIVACY_HIDDEN}
        data-test-mask-controls={JSON.stringify(controls?.toggles?.[mediaKey])}>
        {maskData.map(({key, wbFile, classLabels}) => {
          if (!_.includes(maskOptions?.maskKeys ?? [], key)) {
            return null;
          }
          const maskControls = controls?.toggles?.[mediaKey]?.[key];

          return (
            <SegmentationMaskFromFile
              key={key}
              style={{position: 'absolute', top: 0}}
              maskControls={maskControls}
              maskKey={key}
              classLabels={classLabels}
              runSignature={runSignature}
              mask={wbFile}
              mediaPanelRef={mediaPanelRef}
            />
          );
        })}
      </div>
    );
  }
);

MasksOverlay.displayName = 'MasksOverlay';

export default MasksOverlay;
